import { useState } from "react";
import { atualizarEntrega } from "services/entrega.service";

interface ModalEditarIniciativaProps {
  entrega: any;
  onSave: (entregaAtualizada: any) => void;
  onClose: () => void;
  currentUser?: any;
}

export function ModalEditarIniciativa({
  entrega,
  onSave,
  onClose,
  currentUser,
}: ModalEditarIniciativaProps) {
  const [title, setTitle] = useState(entrega.title || "");
  const [description, setDescription] = useState(entrega.description || "");
  const [bloco, setBloco] = useState(entrega.indicator?.bloco || "");
  const [indicadorNome, setIndicadorNome] = useState(entrega.indicator?.nome || "");
  const [result, setResult] = useState(entrega.result || "");
  const [semestre, setSemestre] = useState<string>(
    entrega.semestre ? String(entrega.semestre) : ""
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const semestreNum = semestre ? Number(semestre) : entrega.semestre;
      const isPrimeiro = semestreNum === 1;

      const logEntry = currentUser ? {
        action: "editada" as const,
        user_id: currentUser._id || "",
        user_name: currentUser.detail?.nome_guerra || currentUser.detail?.nome || "",
        user_matricula: currentUser._id || "",
        timestamp: new Date().toISOString(),
      } : null;

      const approval_log = logEntry
        ? [...(entrega.approval_log || []), logEntry]
        : entrega.approval_log;

      const entregaAtualizada = {
        ...entrega,
        title,
        description,
        result,
        semestre: semestreNum,
        approval_log,
        start: isPrimeiro
          ? new Date("2025-01-01").getTime()
          : new Date("2025-07-01").getTime(),
        end: isPrimeiro
          ? new Date("2025-06-30").getTime()
          : new Date("2025-12-31").getTime(),
        indicator: {
          ...entrega.indicator,
          nome: indicadorNome,
          bloco,
        },
      };

      await atualizarEntrega(entregaAtualizada);
      onSave(entregaAtualizada);
    } catch (error) {
      console.error("Erro ao salvar edição:", error);
      alert("Erro ao salvar as alterações. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black bg-opacity-70"
        onClick={onClose}
      />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Editar Iniciativa
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                Nome da Iniciativa
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={150}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Digite o nome da iniciativa"
              />
              <div className="text-right text-xs text-gray-400 mt-1">
                {title.length}/150
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                Bloco do Conexão
              </label>
              <input
                type="text"
                value={bloco}
                onChange={(e) => setBloco(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Digite o bloco"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                Indicador
              </label>
              <input
                type="text"
                value={indicadorNome}
                onChange={(e) => setIndicadorNome(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Digite o indicador"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                Semestre
              </label>
              <select
                value={semestre}
                onChange={(e) => setSemestre(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Selecione o semestre</option>
                <option value="1">1º Semestre de 2025</option>
                <option value="2">2º Semestre de 2025</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                {`Descrição da Iniciativa ${entrega.categoryPremio === "PILAR" ? "Estruturante" : "Negocial"}`}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                maxLength={2000}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                placeholder="Descreva a iniciativa"
              />
              <div className="text-right text-xs text-gray-400 mt-1">
                {description.length}/2000
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                Pergunta Obrigatória
              </label>
              <p className="text-gray-500 text-xs mb-2" dangerouslySetInnerHTML={{
                __html: `Por que essa iniciativa ${entrega.categoryPremio === "PILAR" ? "Estruturante" : "Negocial"} foi essencial para a sustentação e a evolução do Banco, contribuindo para expressar o Propósito do BB, bem como para a superação do indicador da Plataforma Conexão ${entrega.indicator?.bloco ? `do ${entrega.indicator.bloco}` : ""} <span class="text-blue-600">"${entrega.indicator?.nome || "indicador selecionado"}"</span> ao qual está vinculada?`
              }} />
              <textarea
                value={result}
                onChange={(e) => setResult(e.target.value)}
                rows={5}
                maxLength={2000}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                placeholder="Descreva os resultados alcançados"
              />
              <div className="text-right text-xs text-gray-400 mt-1">
                {result.length}/2000
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
            >
              CANCELAR
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 rounded-lg bg-green-bb-2 hover:bg-green-bb text-blue-bb font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "SALVANDO..." : "SALVAR"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
